import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { OnboardingAPI } from "../api/onboarding";
import type { CompanyProposalState } from "../types";
import { onboardingKeys, useOnboardingClientId } from "./use-onboarding";

export const companyProposalKeys = {
  state: (clientId: string) =>
    [...onboardingKeys.all, "company-proposal", clientId] as const,
};

/**
 * "Remplir depuis mon site" — the last run for this tenant.
 *
 * Polls only while the site is being read. The result is a proposal the form
 * offers; nothing here writes the company.
 */
export function useCompanyProposal(options?: { enabled?: boolean }) {
  const clientId = useOnboardingClientId();

  return useQuery<CompanyProposalState>({
    queryKey: companyProposalKeys.state(clientId),
    queryFn: () => OnboardingAPI.getCompanyProposal(clientId),
    enabled: !!clientId && options?.enabled !== false,
    refetchOnWindowFocus: false,
    refetchInterval: (query) =>
      query.state.data?.status === "running" ? 3_000 : false,
  });
}

export function useStartCompanyProposal() {
  const clientId = useOnboardingClientId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { url?: string }) =>
      OnboardingAPI.startCompanyProposal(clientId, input),
    onSuccess: (state) => {
      queryClient.setQueryData(companyProposalKeys.state(clientId), state);
    },
  });
}

/** The owner applied the proposal or set it aside: stop offering it. */
export function useDismissCompanyProposal() {
  const clientId = useOnboardingClientId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => OnboardingAPI.dismissCompanyProposal(clientId),
    onSuccess: (state) => {
      queryClient.setQueryData(companyProposalKeys.state(clientId), state);
    },
  });
}
